import React from 'react';
import classNames from 'classnames';
import { Tile, TileProps } from '@components/tile/Tile';
import { TileGradient } from '@components/tile/TileGradient';
import { TileItem, ItemPositionType } from '@components/tile/TileItem';
import { TileSelect, TileSelectProps } from '@components/tile/TileSelect';
import './InteractiveTile.scss';
import { TileContentLocked } from '../TileContentLocked';

export interface InteractiveProps extends TileProps, TileSelectProps {
  /** If true, hide the gradient. default false */
  hideGradient?: boolean;
  /** If true, hide the selector. Default false */
  hideSelector?: boolean;
  /** Sets the position for the selector. */
  selectorPosition?: ItemPositionType;
  className?: string;
  /** alt text for screen reader */
  ariaLabel?: string;
  /** if true the tile displays a lock icon */
  isContentLocked?: boolean;
}

/**
 * Interactive Tile Component
 * @param bgProps - Pass props to background component.
 * @param hideGradient - If true, hide the gradient. default false
 * @param hideSelector - If true, hide the selector. default false
 * @param selectorPosition - Sets the position for the selector.
 * @param disableGrowEffect - If true, the grow effect is disabled.
 * @param onClick - onClick callback for the Tile.
 * @param onFocus - onFocus callback for the Tile.
 * @param onBlur - onBlur callback for the Tile.
 * @param checked - If true, the component is checked.
 * @param ariaLabel - for screen reader
 * @param disabled - If true, the component is disabled.
 * @param children - Any React.ReactNode element.
 * @param className - Additional classnames to be included as customization.
 * @param isContentLocked - If true, the tile displays lock icon.
 */
export const InteractiveTile: React.FC<InteractiveProps> = ({
  checked,
  ariaLabel,
  disabled,
  hideGradient = false,
  hideSelector = false,
  selectorPosition = 'top-right',
  className,
  children,
  isContentLocked = false,
  ...rest
}) => {
  const { bgProps } = rest;
  const tileProps = {
    ...rest,
    disabled,
    bgProps: { ...bgProps, opacity: checked ? 'opacity-70' : bgProps?.opacity },
  };

  const contentPickerProps = !hideSelector
    ? {
        'aria-checked': checked,
        'aria-live': 'polite',
        role: 'checkbox',
      }
    : {};

  if (children)
    return (
      <Tile className={classNames('vds-interactive-tile', className)} {...tileProps}>
        {children}
      </Tile>
    );

  return (
    <Tile
      {...tileProps}
      {...contentPickerProps}
      aria-label={ariaLabel}
      className={classNames('vds-interactive-tile', className)}>
      <TileGradient hidden={hideGradient} />
      {!hideSelector && (
        <TileItem position={selectorPosition}>
          <TileSelect checked={checked} />
        </TileItem>
      )}
      {isContentLocked && <TileContentLocked />}
    </Tile>
  );
};
